// What a click on the page is reported as: `ui_click` with the control's
// stable name when the sweep gave it one (`data-track`, with its optional
// `data-track-value`, the nearest up from the target), its reduced label,
// the heading or landmark it sits under, and the page. Only clicks on a
// control count; a click on plain text sends nothing. The DOM stays here;
// what is done with the strings is click-label.ts.
import type { Analytics, AnalyticsValue } from '@chess/api-analytics';
import { labelFrom, reduceLabel, resolveTrack, type TrackAttributes } from './click-label.ts';
import { pagePathOf } from './sites.ts';

export const UI_CLICK = 'ui_click';

/** What counts as a control; an element named by the sweep counts whatever it is. */
const CONTROL = 'button, a[href], [role=button], [role=link], [role=tab], [role=slider], summary, input[type=checkbox], [data-track]';

/** Where a heading names what the control is part of: a card, a section, a sheet, a landmark. */
const REGION = 'section, article, [role=region], [role=dialog], dialog, nav, aside, header, footer, main';

/** The parameters for a click at `target`, or undefined when it was not on a control. */
export function clickParams(target: EventTarget | null, pathname: string): Record<string, AnalyticsValue> | undefined {
  if (!(target instanceof Element)) return undefined;
  const control = target.closest(CONTROL);
  if (!control) return undefined;
  const chain: TrackAttributes[] = [];
  for (let el: Element | null = target; el; el = el.parentElement) {
    chain.push({ name: el.getAttribute('data-track'), value: el.getAttribute('data-track-value') });
  }
  const named = resolveTrack(chain);
  const label = labelFrom({
    ariaLabel: control.getAttribute('aria-label'),
    title: control.getAttribute('title'),
    text: control.textContent || control.closest('label')?.textContent,
  });
  const params: Record<string, AnalyticsValue> = { page: pagePathOf(pathname) };
  if (named) {
    params.name = named.name;
    if (named.value) params.value = named.value;
  }
  if (label) params.label = label;
  const context = contextOf(control);
  if (context) params.context = context;
  return params;
}

/** The nearest ancestor's `aria-label`, or the `h1`/`h2` of the nearest region, reduced like a label. */
function contextOf(control: Element): string | undefined {
  for (let el = control.parentElement; el; el = el.parentElement) {
    const aria = el.getAttribute('aria-label');
    if (aria) return reduceLabel(aria) || undefined;
    if (el.matches(REGION)) {
      const heading = el.querySelector('h1, h2');
      const text = heading?.textContent ?? '';
      if (text.trim()) return reduceLabel(text) || undefined;
    }
  }
  return undefined;
}

/** One listener on the document, in the capture phase, so a control that re-renders on click is still seen; returns the remover. */
export function installClicks(analytics: Analytics): () => void {
  const onClick = (event: MouseEvent) => {
    const params = clickParams(event.target, location.pathname);
    if (params) analytics.track(UI_CLICK, params);
  };
  document.addEventListener('click', onClick, { capture: true, passive: true });
  return () => document.removeEventListener('click', onClick, { capture: true });
}
